import React, { useCallback, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';

import { RenderSkillWidget } from '../../Score/ManageEvaluations/render-skill-widget.component';
import { IRecordCompositeScoreInput } from '../../Score/ManageEvaluations/manage-evaluations.interface';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { CALIBRATION_ROUND } from './my-assignments.constants';
import { messages } from './my-assignments.messages';
import { rawAsWidgetScore } from './assignment-scoring.utils';

export interface ICalibrationSkillRowProps {
  item: ICalibrationAssignmentItem;
  buffered?: IRecordCompositeScoreInput;
  pending?: boolean;
  // item-bound: the parent passes one stable, shared handler to every row instead of a fresh
  // per-item closure, so React.memo below can actually skip unrelated rows on a keystroke.
  onSave: (item: ICalibrationAssignmentItem, raw: string, type: string, advance?: boolean) => void;
  onRemove: (item: ICalibrationAssignmentItem) => void;
}

const CalibrationSkillRowComponent: React.FC<ICalibrationSkillRowProps> = ({ item, buffered, pending, onSave, onRemove }) => {
  const { skill } = item;
  const isServerScored = !!item.compositeScoreId;
  const bufferedRaw = buffered?.raw;
  // Memoizes `widgetScore` so a new object each render doesn't retrigger the number widget's
  // sync effect and clobber an unsaved mid-typed value.
  const widgetScore = useMemo(() => rawAsWidgetScore(bufferedRaw), [bufferedRaw]);

  const handleSave = useCallback(
    (raw: string, type: string, advance?: boolean) => onSave(item, raw, type, advance),
    [item, onSave],
  );
  const handleRemove = useCallback(() => onRemove(item), [item, onRemove]);

  return (
    <Box width="100%" my={1} display="flex" flexDirection="column" alignItems="center">
      {!!skill && <Typography variant="body2" align="center" color="textSecondary">{skill.name}</Typography>}
      {isServerScored ? (
        <Chip label={<IntlMsg {...messages.scored} />} color="primary" />
      ) : !skill ? null : (
        <RenderSkillWidget
          skill={skill}
          score={widgetScore}
          pending={pending}
          roundNum={CALIBRATION_ROUND}
          onRemove={handleRemove}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

// One skill widget per skill beneath a clip. Memoized so an edit to one skill's score doesn't
// re-render every sibling row under the same clip.
export const CalibrationSkillRow = React.memo(CalibrationSkillRowComponent);
